import { useEffect, useMemo, useState, useCallback } from "react";
import { Outlet, useParams } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { animate } from "framer-motion";
import { ErrorBoundary } from "@datadog/browser-rum-react";
import useAuthenticatedCall from "core/hooks/useAuthenticatedCall";
import useAuthenticatedMutation from "core/hooks/useAuthenticatedMutation";
import useNavigate from "core/hooks/useNavigate";
import Error from "core/components/Error";
import { useOrganizationByIdQuery, useProductCatalog } from "core/queries";
import {
  getOrganizationDeployment,
  getOrganizationOnboarding,
  updateOrganizationLicenses,
  deployLicense,
  getCustomerSegments,
  getProductCatalogVersion,
} from "modules/admin/actions";
import useProductLicensingFormState from "modules/admin/OrgProductLicenseForm/useProductLicensingFormState";
import buildStatusObject from "modules/admin/OrgProductLicenseForm/buildStatusObject";
import ProductLicenseHeader from "modules/admin/OrgProductLicenseForm/ProductLicenseHeader";
import {
  DEPLOYMENT_STATUS_MAP,
  REFETCH_SHORT,
  REFETCH_LONG,
  defaultLicenseState,
  productIds,
} from "modules/admin/OrgProductLicenseForm/constants";
import { deepMergeObjects } from "modules/admin/OrgProductLicenseForm/utilities";
import ProductLicenseFormContext from "./ProductLicenseFormContext";
import { getEnv, ENVIRONMENTS } from "common/utilities";
import { toast } from "sonner";

const ProductLicenseFormProvider = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const params = useParams();
  const { orgId: organizationId } = params;

  const [isLicensePanelOpen, setIsLicensePanelOpen] = useState(false);
  const [deploymentInProgressTime, setDeploymentInProgressTime] =
    useState("--:--");
  const [deploymentRefetchInterval, setDeploymentRefetchInterval] =
    useState(REFETCH_LONG);
  const [onboardingRefetchInterval, setOnboardingRefetchInterval] =
    useState(REFETCH_LONG);

  const resetScroll = useCallback(() => {
    animate(window.scrollY, 0, {
      duration: 0.5,
      onUpdate: (scrollY) => window.scrollTo(window.scrollX, scrollY),
    });
  }, []);

  // ──────────────────────────────────────────────────────────────
  // Organization licenses query
  // ──────────────────────────────────────────────────────────────
  const {
    data: productLicenses,
    isLoading: isLoadingLicenses,
    refetch: refetchProductLicenses,
    isRefetching: isRefetchingProductLicenses,
  } = useOrganizationByIdQuery({
    organizationId,
    include: "licenses",
    select: (org) => {
      return org.licenses?.products || {};
    },
  });

  // ──────────────────────────────────────────────────────────────
  // Organization details for customer segment
  // ──────────────────────────────────────────────────────────────
  const { data: orgDetails } = useOrganizationByIdQuery({
    organizationId,
    include: "licenses",
    select: (org) => ({
      id: org?.id,
      customer_segment: org?.customer_segment,
      medicare_licensed: org?.medicare_licensed,
    }),
  });

  // ──────────────────────────────────────────────────────────────
  // Onboarding query (gates the deployment query)
  // ──────────────────────────────────────────────────────────────
  const fetchOnboardingCall = useAuthenticatedCall(getOrganizationOnboarding);
  const { data: onboarding } = useQuery({
    queryKey: ["organization-onboarding", { organizationId }],
    queryFn: fetchOnboardingCall,
    staleTime: onboardingRefetchInterval,
    refetchInterval: onboardingRefetchInterval,
  });

  useEffect(() => {
    if (
      onboardingRefetchInterval === REFETCH_SHORT &&
      [
        "not onboarded",
        "timeout",
        "inactive",
        "unknown",
        "failed",
        "succeeded",
      ].includes(onboarding?.rawStatus)
    ) {
      setOnboardingRefetchInterval(REFETCH_LONG);
    }

    if (
      onboardingRefetchInterval === REFETCH_LONG &&
      ![
        "not onboarded",
        "timeout",
        "inactive",
        "unknown",
        "failed",
        "succeeded",
      ].includes(onboarding?.rawStatus)
    ) {
      setOnboardingRefetchInterval(REFETCH_SHORT);
    }
  }, [onboarding?.rawStatus, onboardingRefetchInterval]);

  // ──────────────────────────────────────────────────────────────
  // Deployment query with adaptive polling
  // ──────────────────────────────────────────────────────────────
  const fetchDeploymentCall = useAuthenticatedCall(getOrganizationDeployment);
  const selectDeployment = useCallback(
    (data) => buildStatusObject(data, DEPLOYMENT_STATUS_MAP),
    []
  );
  const {
    isLoading: isDeploymentFetching,
    data: licenseDeployment,
    refetch: refetchDeploymentStatus,
    isRefetching: isRefetchingDeploymentStatus,
  } = useQuery({
    queryKey: ["organization-deployment", { organizationId }],
    queryFn: fetchDeploymentCall,
    staleTime: deploymentRefetchInterval,
    refetchInterval: deploymentRefetchInterval,
    enabled: onboarding && onboarding.rawStatus === "succeeded",
    select: selectDeployment,
    // Disable structural sharing because the select produces DateTime instances
    // which are class objects that cannot be structurally compared
    structuralSharing: false,
  });

  useEffect(() => {
    if (
      deploymentRefetchInterval === REFETCH_SHORT &&
      [
        "not deployed",
        "timeout",
        "inactive",
        "unknown",
        "failed",
        "succeeded",
      ].includes(licenseDeployment?.rawStatus)
    ) {
      setDeploymentRefetchInterval(REFETCH_LONG);
    }

    if (
      deploymentRefetchInterval === REFETCH_LONG &&
      ![
        "not deployed",
        "timeout",
        "inactive",
        "unknown",
        "failed",
        "succeeded",
      ].includes(licenseDeployment?.rawStatus)
    ) {
      setDeploymentRefetchInterval(REFETCH_SHORT);
    }
  }, [licenseDeployment?.rawStatus, deploymentRefetchInterval]);

  // ──────────────────────────────────────────────────────────────
  // Deployment in-progress timer
  // ──────────────────────────────────────────────────────────────
  const deploymentRawStatus = licenseDeployment?.rawStatus;
  const deploymentStart = licenseDeployment?.licenseDeploymentStart;

  useEffect(() => {
    let intervalId;

    if (
      deploymentRawStatus === "in progress" &&
      !isRefetchingProductLicenses &&
      deploymentStart
    ) {
      intervalId = setInterval(() => {
        const now = DateTime.now();
        const duration = now.diff(deploymentStart, [
          "seconds",
          "minutes",
          "hours",
        ]);
        const { hours } = duration.toObject();

        if (hours === 0) {
          setDeploymentInProgressTime(duration.toFormat("mm:ss"));
        } else {
          setDeploymentInProgressTime(duration.toFormat("hh:mm:ss"));
        }
      }, 1000);
    } else {
      setDeploymentInProgressTime("--:--");
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [deploymentRawStatus, deploymentStart, isRefetchingProductLicenses]);

  // ──────────────────────────────────────────────────────────────
  // License deployment retry mutation
  // ──────────────────────────────────────────────────────────────
  const retryLicenseDeployment = useAuthenticatedMutation((req) =>
    deployLicense({ ...req, organizationId })
  );

  const licenseDeployRetryMutation = useMutation({
    mutationFn: retryLicenseDeployment,
    onSuccess: async () => {
      refetchDeploymentStatus();
      toast.success("License deployment retry has been started.");
    },
    onError: async (error, variables, context) => {
      console.error("licenseDeployRetryMutation", {
        error,
        variables,
        context,
      });
      toast.error("License deployment retry failed.");
    },
  });

  // ──────────────────────────────────────────────────────────────
  // Customer segment query
  // ──────────────────────────────────────────────────────────────
  const fetchCustomerSegments = useAuthenticatedCall(getCustomerSegments);
  const { data: selectedCustomerSegment } = useQuery({
    queryKey: ["customer-segments"],
    queryFn: fetchCustomerSegments,
    enabled: !!orgDetails?.customer_segment,
    select: (data) => {
      const customerSegment = Object.values(data).find(
        (item) => item.id === orgDetails?.customer_segment
      );
      const customerSegmentName = customerSegment?.name;

      const isMedicareLicensable = Object.values(data).find(
        (item) => item.id === orgDetails?.customer_segment
      )?.is_medicare_licensable;

      const env = getEnv();
      const isDevEnv = [ENVIRONMENTS.LOCAL, ENVIRONMENTS.DEV].includes(env);
      const isPrOrg = isDevEnv && customerSegment?.pr_org === orgDetails?.id;
      const licensableProductIds = customerSegment?.licensable_products;

      return {
        customerSegmentName,
        licensableProductIds,
        isMedicareLicensable,
        isPrOrg,
      };
    },
  });

  const fetchCatalogVersion = useAuthenticatedCall(getProductCatalogVersion);
  const { data: prNumberForPrCatalog } = useQuery({
    queryKey: ["pr-catalog-version", { isPrCatalog: true }],
    queryFn: fetchCatalogVersion,
    enabled: !!selectedCustomerSegment?.isPrOrg,
    select: (data) => {
      return data?.pr_number;
    },
  });

  // Build customerSegment prop shape (matches what OrganizationLayout passes)
  const customerSegment = useMemo(
    () => ({
      segment: orgDetails?.customer_segment,
      medicareLicensed: orgDetails?.medicare_licensed,
      isPrOrg: selectedCustomerSegment?.isPrOrg,
      prNumber: prNumberForPrCatalog,
    }),
    [orgDetails, selectedCustomerSegment?.isPrOrg, prNumberForPrCatalog]
  );

  const customerSegmentName = selectedCustomerSegment?.customerSegmentName;

  // ──────────────────────────────────────────────────────────────
  // Customer segment products query (for filtering)
  // ──────────────────────────────────────────────────────────────
  const {
    data: selectedCustomerSegmentProducts,
    isLoading: isLoadingSegments,
  } = useQuery({
    queryKey: ["customer-segments"],
    queryFn: fetchCustomerSegments,
    enabled: !!customerSegment?.segment,
    select: (data) => {
      const result = Object.values(data).find(
        (item) => item.id === customerSegment?.segment
      )?.licensable_products;

      return result;
    },
  });

  // ──────────────────────────────────────────────────────────────
  // Product catalog query
  // ──────────────────────────────────────────────────────────────
  const {
    error: catalogError,
    catalog: catalogByProduct,
    isLoading: isLoadingCatalog,
  } = useProductCatalog({
    isPrCatalog: customerSegment?.isPrOrg,
    select: (data) => {
      const filteredProducts = Object.keys(data).filter((productId) =>
        productIds.includes(productId)
      );

      return filteredProducts.reduce((catalogByProduct, productId) => {
        return {
          ...catalogByProduct,
          [productId]: data[productId],
        };
      }, {});
    },
  });

  // ──────────────────────────────────────────────────────────────
  // Form state
  // ──────────────────────────────────────────────────────────────
  const formState = useProductLicensingFormState({
    defaultState: defaultLicenseState,
    serverState: productLicenses,
    productCatalog: catalogByProduct,
    isLoading: isLoadingLicenses || isLoadingCatalog || isLoadingSegments,
  });

  const { getPreparedLicenses, clearAuditTrail, importLicense } = formState;

  // ──────────────────────────────────────────────────────────────
  // Product license mutation (with optimistic updates)
  // ──────────────────────────────────────────────────────────────
  const updateProductLicenses = useAuthenticatedMutation((req) => {
    return updateOrganizationLicenses({
      ...req,
      licenses: {
        products: req.products,
      },
      organizationId,
    });
  });

  const productLicenseMutation = useMutation({
    mutationFn: updateProductLicenses,
    onMutate: (variables) => {
      queryClient.setQueryData(
        [
          "organizations",
          {
            organizationId,
            include: "licenses",
          },
        ],
        (previousData) => {
          // The cache entry can be missing or licence-less: placeholderData is
          // never written to the cache, so a pending or failed org fetch leaves
          // this undefined while the form still renders as loaded. Nothing to
          // update optimistically in that case — leave the cache alone so the
          // refetch on settle is what fills it in.
          if (!previousData?.licenses) {
            return previousData;
          }

          const optimisticData = {
            ...previousData,
            licenses: {
              ...previousData.licenses,
              products: deepMergeObjects(
                previousData.licenses.products,
                variables.products
              ),
              _archivedProducts: previousData.licenses.products,
            },
          };

          return optimisticData;
        }
      );
    },
    onSuccess: async () => {
      refetchProductLicenses();
      refetchDeploymentStatus();

      toast.success(
        "Product Licenses updated. A deployment has been triggered."
      );

      setTimeout(() => {
        clearAuditTrail();
        navigate("./");
      }, 1500);
    },
    onError: async (error) => {
      console.error(error);

      queryClient.setQueryData(
        [
          "organizations",
          {
            organizationId,
            include: "licenses",
          },
        ],
        (previousData) => {
          // onError also fires when onMutate itself threw, in which case there
          // is no archive to roll back to — and the guard above means there may
          // never have been one. Restoring `undefined` here would wipe the
          // products out of the cache, so leave the entry untouched.
          if (!previousData?.licenses?._archivedProducts) {
            return previousData;
          }

          const rollbackData = {
            ...previousData,
            licenses: {
              ...previousData.licenses,
              products: previousData.licenses._archivedProducts,
            },
          };
          delete rollbackData.licenses._archivedProducts;

          return rollbackData;
        }
      );
    },
    onSettled: () => {
      resetScroll();
    },
  });

  // ──────────────────────────────────────────────────────────────
  // Context value
  // ──────────────────────────────────────────────────────────────
  const contextValue = useMemo(
    () => ({
      formState,
      licenseDeployment,
      isDeploymentFetching,
      refetchDeploymentStatus,
      licenseDeployRetryMutation,
      productLicenseMutation,
      customerSegment,
      customerSegmentName,
      selectedCustomerSegmentProducts,
      productLicenses,
      isLoadingLicenses,
      refetchProductLicenses,
      isRefetchingProductLicenses,
      catalogByProduct,
      catalogError,
      isLoadingCatalog,
      isLoadingSegments,
    }),
    // Note: licenseDeployRetryMutation and productLicenseMutation are
    // intentionally tracked by their meaningful state properties (isPending,
    // status) rather than by object identity because useMutation returns a new
    // object on every render, which would defeat memoization.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      formState,
      licenseDeployment,
      isDeploymentFetching,
      refetchDeploymentStatus,
      licenseDeployRetryMutation.isPending,
      licenseDeployRetryMutation.status,
      productLicenseMutation.isPending,
      productLicenseMutation.status,
      customerSegment,
      customerSegmentName,
      selectedCustomerSegmentProducts,
      productLicenses,
      isLoadingLicenses,
      refetchProductLicenses,
      isRefetchingProductLicenses,
      catalogByProduct,
      catalogError,
      isLoadingCatalog,
      isLoadingSegments,
    ]
  );

  return (
    <ProductLicenseFormContext.Provider value={contextValue}>
      <ProductLicenseHeader
        licenseDeployment={licenseDeployment}
        customerSegment={customerSegment}
        customerSegmentName={customerSegmentName}
        isLicensePanelOpen={isLicensePanelOpen}
        setIsLicensePanelOpen={setIsLicensePanelOpen}
        deploymentInProgressTime={deploymentInProgressTime}
        productLicenses={productLicenses}
        getPreparedLicenses={getPreparedLicenses}
        importLicense={importLicense}
      />
      <ErrorBoundary
        onError={console.error}
        fallback={
          <Error>
            We encountered an issue while attempting to load this page. Please
            contact support for assistance.
          </Error>
        }
      >
        <Outlet />
      </ErrorBoundary>
    </ProductLicenseFormContext.Provider>
  );
};

export default ProductLicenseFormProvider;
